import React, { createContext, useContext, useMemo } from 'react'
import { isMobileDevice } from '../utils/mobile'

interface FloatingContextValue {
  /**
   * Whether the widget is in floating bar mode (mobile or regular).
   * This affects layout, height management, and styling across all components.
   */
  isFloating: boolean
  
  /**
   * Whether we're on a mobile device (regardless of floating mode)
   */
  isMobile: boolean
  
  /**
   * Configuration for floating mode behavior
   */
  floatingConfig: {
    maxHeight?: string
    position?: 'bottom' | 'top'
    animation?: boolean
  }
}

const FloatingContext = createContext<FloatingContextValue | undefined>(undefined)

interface FloatingProviderProps {
  children: React.ReactNode
  /**
   * Whether mobile floating bar mode is enabled in config
   */
  mobileFloatingBar?: boolean
  /**
   * Widget position configuration
   */
  position?: 'inline' | 'bottom-right' | 'bottom-left'
}

/**
 * Provider for centralized floating state management.
 * Determines floating state once and provides it to all child components.
 */
export function FloatingProvider({ 
  children, 
  mobileFloatingBar,
  position 
}: FloatingProviderProps) {
  const value = useMemo<FloatingContextValue>(() => {
    const isMobile = isMobileDevice()
    // Widget is floating if:
    // 1. Mobile device with mobileFloatingBar enabled, OR
    // 2. Regular floating position (bottom-right, bottom-left)
    const isFloating = (isMobile && mobileFloatingBar === true) || 
                      (position === 'bottom-right' || position === 'bottom-left')
    
    return {
      isFloating,
      isMobile,
      floatingConfig: {
        // In floating mode, limit height to 70% of viewport (increased from 50% for better usability)
        maxHeight: isFloating ? '70vh' : undefined,
        // Floating widgets are always at bottom
        position: isFloating ? 'bottom' : undefined,
        // Enable animations in floating mode
        animation: isFloating
      }
    }
  }, [mobileFloatingBar, position])
  
  return (
    <FloatingContext.Provider value={value}>
      {children}
    </FloatingContext.Provider>
  )
}

/**
 * Hook to access floating state throughout the component tree
 */
export function useFloating() {
  const context = useContext(FloatingContext)
  
  if (!context) {
    throw new Error('useFloating must be used within FloatingProvider')
  }
  
  return context
}

/**
 * Utility function to generate className based on floating state
 */
export function getFloatingClassName(
  isFloating: boolean, 
  normalClass: string, 
  floatingClass: string
): string {
  return isFloating ? floatingClass : normalClass
}
